/**
 * Formats employee range for display
 * @param employees Employee range string (e.g., "1-10", "10-50", "50-200", "200-500", "500-1000", "1000+")
 * @returns Formatted employee range
 */
export const formatEmployeeRange = (employees: string): string => {
  if (!employees) return 'Unknown';
  
  // If it's already a formatted range, return it
  if (employees.includes('-') || employees.includes('+')) {
    return employees;
  }
  
  // Try to parse as a number
  const count = parseInt(employees, 10);
  if (isNaN(count)) return employees;
  
  // Format based on count
  if (count < 10) return '1-10';
  if (count < 50) return '10-50';
  if (count < 200) return '50-200';
  if (count < 500) return '200-500';
  if (count < 1000) return '500-1000';
  return '1000+';
};

/**
 * Formats a currency value for display
 * @param amount The amount to format
 * @param currency The currency code (e.g., "USD", "EUR")
 * @param options Additional formatting options
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = 'USD', options: Intl.NumberFormatOptions = {}): string => {
  if (amount === null || amount === undefined) return '';
  
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options
    });
    
    return formatter.format(amount);
  } catch (error) {
    // Fallback to basic formatting if Intl is not supported
    const symbol = currency === 'USD' ? '$' : currency + ' ';
    return `${symbol}${amount.toFixed(2)}`;
  }
};
